/**
 * Stepper — F-003.2 Phase 3 (IMPL-P3-003).
 *
 * Indicatore di avanzamento wizard con semantica corretta per screen reader:
 *  - <nav aria-label="Passaggi pratica"> — wizard non è una tab bar
 *  - <ol> con <li aria-current="step"> sul passo attivo
 *  - Passi completati: aria-label="Passo N completato: <Nome>"
 *  - I passi non sono cliccabili — la navigazione è avanti/indietro
 *
 * Mobile (<md): indicatore collassato "Passo N/M — Nome"
 * Desktop (md+): tutti i passi visibili nella <ol>
 */

export interface StepperProps {
  steps: ReadonlyArray<string>;
  currentStep: number;
}

export function Stepper({ steps, currentStep }: StepperProps) {
  return (
    <nav aria-label="Passaggi pratica" className="w-full">
      {/* Mobile: indicatore collassato */}
      <p className="md:hidden text-sm text-muted-foreground">
        <span className="font-medium">
          Passo {currentStep + 1}/{steps.length}
        </span>
        {' — '}
        {steps[currentStep]}
      </p>

      {/* Desktop: tutti i passi in riga */}
      <ol className="hidden md:flex items-center w-full">
        {steps.map((label, idx) => {
          const isCurrent = idx === currentStep;
          const isDone = idx < currentStep;

          return (
            <li
              key={label}
              aria-current={isCurrent ? 'step' : undefined}
              aria-label={isDone ? `Passo ${idx + 1} completato: ${label}` : undefined}
              className="flex items-center flex-1 min-w-0"
            >
              {/* Connettore linea (non per il primo elemento) */}
              {idx > 0 && (
                <div
                  className={`h-px flex-shrink-0 w-4 mx-1 ${
                    isDone || isCurrent ? 'bg-primary' : 'bg-border'
                  }`}
                  aria-hidden="true"
                />
              )}

              <div className="flex items-center gap-1.5 min-w-0">
                {/* Numero step con indicatore visivo */}
                <span
                  aria-hidden="true"
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    isCurrent
                      ? 'bg-primary text-primary-foreground'
                      : isDone
                        ? 'bg-primary/20 text-primary'
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {idx + 1}
                </span>

                {/* Label passo */}
                <span
                  className={`text-sm truncate ${
                    isCurrent
                      ? 'font-medium text-foreground'
                      : isDone
                        ? 'text-muted-foreground'
                        : 'text-muted-foreground/60'
                  }`}
                >
                  {label}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
